import React from 'react';
import { getCurrencySymbol } from '../../utils/currencyUtils';
import { itemTypeBadgeClass, ITEMS_THEME } from './itemsTheme';
import { statLabelCls } from './ItemsHeadings';
import {
  ChevronDown,
  ChevronUp,
  Layers,
  Pencil,
  Trash2,
} from 'lucide-react';

export default function ItemCard({
  item,
  liveStock,
  companyCurrency,
  isSelected,
  onToggleSelect,
  isExpanded,
  onToggle,
  onEdit,
  onDelete,
}) {
  const title = item.itemName || 'Item';
  const initial = (title || 'I').charAt(0).toUpperCase();
  const rate = `${getCurrencySymbol(companyCurrency)}${item.defaultRate ?? 0}`;

  return (
    <div className="rounded-xl border border-amber-100/80 bg-gradient-to-br from-amber-50/30 to-white shadow-sm overflow-hidden mb-2">
      <div className="flex items-stretch gap-1 p-3">
        <label
          className="flex items-center shrink-0 px-1 cursor-pointer"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect(item.id)}
            className={`h-4 w-4 rounded border-gray-300 ${ITEMS_THEME.checkbox}`}
            aria-label={`Select ${title}`}
          />
        </label>
        <div
          className="flex-1 min-w-0 cursor-pointer active:bg-slate-50/80 transition-colors rounded-lg"
          onClick={() => onToggle(item.id)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && onToggle(item.id)}
        >
          <div className="flex items-start gap-3">
            <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${ITEMS_THEME.avatarGradient} flex items-center justify-center text-white font-bold text-xs flex-shrink-0 shadow`}>
              {initial}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-sm font-bold text-gray-900 truncate">{title}</h3>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ring-1 ring-inset ${itemTypeBadgeClass(item.itemType)}`}
                >
                  {item.itemType}
                </span>
                <span
                  className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                    item.isActive !== false ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'
                  }`}
                >
                  {item.isActive !== false ? 'Active' : 'Inactive'}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1 flex flex-wrap items-center gap-x-1.5">
                <span className="inline-flex items-center gap-1">
                  <Layers className="w-3 h-3 shrink-0" aria-hidden />
                  {item.quantityMeasurement}
                </span>
                <span className="text-gray-300">·</span>
                <span>Stock {liveStock}</span>
                {item.hsnCode && (
                  <>
                    <span className="text-gray-300">·</span>
                    <span>HSN {item.hsnCode}</span>
                  </>
                )}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2 flex-shrink-0">
              <div className="text-right">
                <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Rate</p>
                <p className={`text-base font-bold leading-tight tabular-nums ${ITEMS_THEME.amount}`}>{rate}</p>
              </div>
              <div className="flex items-center gap-0.5">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(item);
                  }}
                  className={`p-2 min-h-[44px] min-w-[44px] inline-flex items-center justify-center ${ITEMS_THEME.editIcon} rounded-lg transition-colors`}
                  title="Edit"
                  aria-label={`Edit ${title}`}
                >
                  <Pencil className="w-4 h-4" aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(item.id);
                  }}
                  className="p-2 min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete"
                  aria-label={`Delete ${title}`}
                >
                  <Trash2 className="w-4 h-4" aria-hidden />
                </button>
                <span className="p-2 text-gray-400" aria-hidden>
                  {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-amber-100/80 p-4 bg-amber-50/25">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-3">
              <h4 className={`${statLabelCls} mb-2`}>Tax</h4>
              <p className="text-gray-800">GST {typeof item.gstPercentage !== 'undefined' ? `${item.gstPercentage}%` : '0%'}</p>
              <p className="text-gray-600 text-xs mt-1">Category: {item.category || '—'}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-3">
              <h4 className={`${statLabelCls} mb-2`}>Identifiers</h4>
              <p className="text-gray-800 break-all">Barcode: {item.barcodeNumber || '—'}</p>
            </div>
            {item.description && (
              <div className="col-span-2 bg-white rounded-xl border border-slate-100 shadow-sm p-3">
                <h4 className={`${statLabelCls} mb-1`}>Description</h4>
                <p className="text-gray-700 text-sm">{item.description}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
